<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportMetricCard from 'dashboard/routes/dashboard/settings/reports/components/ReportMetricCard.vue';
import BroadcastsAPI from 'dashboard/api/broadcasts';
import {
  estimatedTemplatePrice,
  formatEstimatedPrice,
} from 'dashboard/helper/whatsappPricing';
import {
  buildTemplateParameters,
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';
import TemplateAlerts from 'dashboard/routes/dashboard/settings/inbox/settingsPage/whatsappTemplates/TemplateAlerts.vue';
import FlowsAPI from 'dashboard/api/flows';
import { isTemplateCapableWhatsappInbox } from 'dashboard/helper/whatsappCampaignInbox';
import SegmentAudiencePicker from 'dashboard/components-next/filter/SegmentAudiencePicker.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
// `t` ja e nome de variavel local neste arquivo (textos de modelo).
const { t: translate } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

// dicas de texto (constantes p/ nao quebrar o parser do template com chaves)
const nomeHint = '{{nome}}';
const primeiroNomeHint = '{{primeiro_nome}}';
const spinHint = '{ola|oi}';
// variaveis de contato resolvidas por destinatario no envio; colunas do CSV e
// chaves de additional_attributes do contato tambem viram variaveis.
const CONTACT_VARS = [
  { hint: '{{primeiro_nome}}', desc: 'Só o primeiro nome do contato' },
  { hint: '{{nome}}', desc: 'Nome completo do contato' },
  { hint: '{{telefone}}', desc: 'Telefone do contato' },
  { hint: '{{email}}', desc: 'Email do contato' },
];

const broadcasts = ref([]);
// flows da conta (para o seletor 'continuar no flow apos a resposta')
const accountFlows = ref([]);
const loading = ref(false);
const showForm = ref(false);

const emptyForm = () => ({
  name: '',
  inbox_id: '',
  message_mode: 'text',
  content: '',
  template_name: '',
  // valores das variaveis do template selecionado ({{1}}, {{2}}...)
  template_vars: {},
  // valores dos parametros de botao (URL dinamica/copy code), por indice REAL no template
  template_button_params: {},
  audience_type: 'all',
  audience: {
    label_ids: [],
    contact_ids: [],
    rows: [],
    custom_filter_id: null,
  },
  min_interval_seconds: 5,
  max_interval_seconds: 15,
  scheduled_at: '',
  // flow que continua a conversa quando o cliente RESPONDE ao disparo
  follow_up_flow_id: '',
  // sequencia (Fase S): mensagens de follow-up apos a base
  steps: [],
});
const form = ref(emptyForm());
const saving = ref(false);
// Previa do publico "Segmento" (spec 007): bloqueia salvar/disparar quando responde erro.
const segmentPreview = ref({ valid: false, reachableCount: 0 });
// edicao de um disparo existente (null = criando novo)
const editingId = ref(null);
const removedStepIds = ref([]);

// midia opcional do disparo (upload -> blob_id)
const media = ref({ blob_id: '', file_url: '', filename: '' });
const mediaUploading = ref(false);

// relatorio (destinatarios + contadores + metricas) de um disparo
const report = ref({
  open: false,
  broadcast: null,
  recipients: [],
  loading: false,
  metrics: null,
  metricsLoading: false,
});
// aba interna do modal de relatorio: 'resumo' | 'destinatarios'
const reportTab = ref('resumo');

const STATUS_LABELS = {
  draft: 'Rascunho',
  scheduled: 'Agendado',
  processing: 'Enviando',
  paused: 'Pausado',
  cancelled: 'Cancelado',
  completed: 'Concluído',
  failed: 'Falhou',
  sent: 'Enviado',
  pending: 'Pendente',
  skipped: 'Ignorado',
};
// Sem tela de criação desde D11: só rascunho e agendado ainda podem ser ajustados.
const EDITABLE_STATUSES = ['draft', 'scheduled'];
const statusLabel = s => STATUS_LABELS[s] || s;
const STATUS_STYLES = {
  draft: 'bg-n-slate-3 text-n-slate-11',
  scheduled: 'bg-n-amber-3 text-n-amber-11',
  processing: 'bg-n-teal-3 text-n-teal-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
  completed: 'bg-n-teal-3 text-n-teal-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
};
const statusClass = s => STATUS_STYLES[s] || 'bg-n-slate-3 text-n-slate-11';

const formatSchedule = dt => {
  if (!dt) return '';
  const d = new Date(dt);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};
// converte ISO -> valor aceito pelo input datetime-local (hora local)
const toLocalInput = dt => {
  if (!dt) return '';
  const d = new Date(dt);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// preview ao vivo da mensagem (estilo WhatsApp)
const SAMPLE_VARS = {
  nome: 'Juliana Ribeiro',
  name: 'Juliana Ribeiro',
  primeiro_nome: 'Juliana',
  first_name: 'Juliana',
  telefone: '+55 11 90000-0000',
  email: 'juliana@email.com',
};
const resolveVars = t =>
  t.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => SAMPLE_VARS[k] || m);
// spintax: mostra a 1a variacao (preview estavel)
const resolveSpintax = t =>
  t.replace(/\{([^{}]*\|[^{}]*)\}/g, (m, g) => g.split('|')[0]);
const mediaIsImage = computed(() =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(media.value.filename || '')
);
const isImageName = name => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');
const previewSteps = computed(() =>
  form.value.steps.map(s => ({
    text: resolveSpintax(resolveVars(s.content || '')),
    delay: s.delay_after_seconds,
    media: s.media,
    isImage: isImageName(s.media.filename),
  }))
);

const handleMedia = async event => {
  const file = event.target.files[0];
  if (!file) return;
  mediaUploading.value = true;
  try {
    const { data } = await BroadcastsAPI.uploadMedia(file);
    media.value = {
      blob_id: data.blob_id,
      file_url: data.file_url,
      filename: file.name,
    };
    useAlert('Mídia carregada');
  } catch (e) {
    useAlert('Erro ao carregar a mídia');
  } finally {
    mediaUploading.value = false;
  }
};

// sequencia (Fase S)
const addStep = () => {
  form.value.steps.push({
    content: '',
    delay_after_seconds: 30,
    media: { blob_id: '', file_url: '', filename: '' },
  });
};
const removeStep = idx => {
  const s = form.value.steps[idx];
  if (s?.id) removedStepIds.value.push(s.id);
  form.value.steps.splice(idx, 1);
};
const handleStepMedia = async (event, idx) => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const { data } = await BroadcastsAPI.uploadMedia(file);
    form.value.steps[idx].media = {
      blob_id: data.blob_id,
      file_url: data.file_url,
      filename: file.name,
    };
    useAlert('Mídia do passo carregada');
  } catch (e) {
    useAlert('Erro ao carregar a mídia do passo');
  }
};

const selectedInbox = computed(() =>
  inboxes.value.find(i => i.id === Number(form.value.inbox_id))
);
const isOfficialWhatsapp = computed(() => {
  return isTemplateCapableWhatsappInbox(selectedInbox.value);
});

// templates aprovados sincronizados do canal oficial (Meta)
const selectedTemplateId = ref('');
const templateId = t => `${t.name}|${t.language}`;
const approvedTemplates = computed(() =>
  (selectedInbox.value?.message_templates || []).filter(
    t => t.status === 'APPROVED'
  )
);
const selectedTemplate = computed(() =>
  approvedTemplates.value.find(t => templateId(t) === selectedTemplateId.value)
);
const templatePriceBadge = computed(() => {
  const price = estimatedTemplatePrice(selectedTemplate.value?.category);
  if (price === null || price === undefined) return '';
  return `~${formatEstimatedPrice(price)}/msg · ${selectedTemplate.value.category}`;
});
const templateBody = computed(
  () =>
    selectedTemplate.value?.components?.find(c => c.type === 'BODY')?.text || ''
);
// variaveis do corpo do template: {{1}}, {{2}} ou nomeadas
const TEMPLATE_VAR_RE = /\{\{\s*([\w.]+)\s*\}\}/g;
const templateVarKeys = computed(() => {
  return [...templateBody.value.matchAll(TEMPLATE_VAR_RE)].reduce(
    (keys, match) => (keys.includes(match[1]) ? keys : [...keys, match[1]]),
    []
  );
});
const varHint = k => `{{${k}}}`;
// insercao de variavel de contato por clique: no modo template entra no campo
// de variavel focado por ultimo ({{1}}, {{2}}...); no modo texto, no fim da mensagem.
const activeVarKey = ref('');
const insertContactVar = hint => {
  if (form.value.message_mode === 'template') {
    const k =
      activeVarKey.value && templateVarKeys.value.includes(activeVarKey.value)
        ? activeVarKey.value
        : templateVarKeys.value[0];
    if (!k) return;
    form.value.template_vars[k] = (form.value.template_vars[k] || '') + hint;
  } else {
    form.value.content = (form.value.content || '') + hint;
  }
};
const filledTemplateBody = () =>
  templateBody.value.replace(
    TEMPLATE_VAR_RE,
    (m, k) => form.value.template_vars[k] || m
  );
const previewText = computed(() => {
  if (form.value.message_mode === 'template') {
    if (templateBody.value)
      return resolveSpintax(resolveVars(filledTemplateBody()));
    return form.value.template_name
      ? `[Template: ${form.value.template_name}]`
      : '';
  }
  return resolveSpintax(resolveVars(form.value.content || ''));
});

// botoes do template que exigem parametro (URL com variavel ou copy code);
// buildTemplateParameters ja devolve o array denso com o indice REAL no template.
const templateButtonFields = computed(() => {
  if (!selectedTemplate.value) return [];
  const fields =
    buildTemplateParameters(selectedTemplate.value, false).buttons || [];
  const original =
    findComponentByType(selectedTemplate.value, COMPONENT_TYPES.BUTTONS)
      ?.buttons || [];
  return fields.map(b => ({
    ...b,
    label: original[b.index]?.text || `Botão ${b.index + 1}`,
  }));
});
const missingButtonParams = () =>
  form.value.message_mode === 'template' &&
  templateButtonFields.value.some(
    b => !(form.value.template_button_params[b.index] || '').trim()
  );
// todos os botoes do template no preview (inclusive quick replies)
const previewButtons = computed(() => {
  if (form.value.message_mode !== 'template' || !selectedTemplate.value)
    return [];
  const buttons =
    findComponentByType(selectedTemplate.value, COMPONENT_TYPES.BUTTONS)
      ?.buttons || [];
  return buttons.map(b => b.text);
});

// enquanto carrega um disparo p/ edicao, nao deixar os watches abaixo zerarem
// o template/variaveis que o openEdit acabou de preencher.
const loadingEdit = ref(false);
watch(selectedTemplateId, () => {
  if (loadingEdit.value) return;
  form.value.template_name =
    selectedTemplate.value?.name || form.value.template_name;
  form.value.template_vars = {};
  form.value.template_button_params = {};
});
watch(
  () => form.value.inbox_id,
  () => {
    if (loadingEdit.value) return;
    selectedTemplateId.value = '';
  }
);
// API oficial exige template; demais canais enviam texto de sessao.
// O tipo de mensagem e derivado do canal (sem select manual).
watch(isOfficialWhatsapp, official => {
  if (loadingEdit.value) return;
  form.value.message_mode = official ? 'template' : 'text';
});

// modelo de planilha para importacao (colunas certas, sem erro de formato)
const empresaHint = '{{empresa}}';
const downloadCsvModel = () => {
  const rows = [
    'telefone,nome,empresa',
    '+5511999990001,Maria Silva,Empresa X',
    '+5511988880002,João Souza,Empresa Y',
  ];
  const blob = new Blob([`\uFEFF${rows.join('\n')}\n`], {
    type: 'text/csv;charset=utf-8;',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'modelo-disparo-vincto.csv';
  a.click();
  URL.revokeObjectURL(a.href);
};

// enquanto houver disparo Enviando/Agendado, atualiza a lista sozinho
let pollTimer = null;
let isMounted = true;
let fetchBroadcasts;
const schedulePoll = () => {
  clearTimeout(pollTimer);
  if (!isMounted) return;
  const active = broadcasts.value.some(b =>
    ['processing', 'scheduled'].includes(b.status)
  );
  if (active) pollTimer = setTimeout(() => fetchBroadcasts(), 5000);
};

fetchBroadcasts = async () => {
  loading.value = true;
  try {
    const { data } = await BroadcastsAPI.get();
    broadcasts.value = data;
    schedulePoll();
  } catch (e) {
    useAlert('Erro ao carregar disparos');
  } finally {
    loading.value = false;
  }
};

// carrega um disparo existente no formulario (editar)
const openEdit = async b => {
  loadingEdit.value = true;
  segmentPreview.value = { valid: false, reachableCount: 0 };
  form.value = {
    ...emptyForm(),
    name: b.name,
    inbox_id: b.inbox_id,
    message_mode: b.message_mode,
    content: b.message_mode === 'template' ? '' : b.content || '',
    template_name: b.template_name || '',
    audience_type: b.audience_type,
    audience: {
      label_ids: b.audience?.label_ids || [],
      contact_ids: b.audience?.contact_ids || [],
      rows: b.audience?.rows || [],
      custom_filter_id: b.audience?.custom_filter_id ?? null,
    },
    min_interval_seconds: b.min_interval_seconds,
    max_interval_seconds: b.max_interval_seconds,
    scheduled_at: toLocalInput(b.scheduled_at),
    follow_up_flow_id: b.follow_up_flow_id || '',
    steps: (b.steps || []).map(s => ({
      id: s.id,
      content: s.content || '',
      delay_after_seconds: s.delay_after_seconds,
      media: {
        blob_id: '',
        file_url: s.attachment?.file_url || '',
        filename: s.attachment?.filename || '',
      },
    })),
  };
  media.value = {
    blob_id: '',
    file_url: b.attachment?.file_url || '',
    filename: b.attachment?.filename || '',
  };
  editingId.value = b.id;
  removedStepIds.value = [];
  const tp = b.template_params;
  selectedTemplateId.value =
    tp?.name && tp?.language ? `${tp.name}|${tp.language}` : '';
  showForm.value = true;
  await nextTick();
  if (tp?.processed_params?.body) {
    form.value.template_vars = { ...tp.processed_params.body };
  }
  if (Array.isArray(tp?.processed_params?.buttons)) {
    form.value.template_button_params = Object.fromEntries(
      tp.processed_params.buttons
        .filter(button => button)
        .map((button, index) => [button.index ?? index, button.parameter || ''])
    );
  }
  // libera os watches só depois que o flush inicial (inbox_id/template) passou
  loadingEdit.value = false;
};

const handleCsv = async event => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const { data } = await BroadcastsAPI.importCsv(file);
    form.value.audience.rows = data.rows;
    useAlert(`${data.count} contatos importados do CSV`);
  } catch (e) {
    useAlert('Erro ao importar CSV');
  }
};

const buildPayload = () => {
  const audience = {};
  if (form.value.audience_type === 'labels') {
    audience.label_ids = form.value.audience.label_ids;
  } else if (form.value.audience_type === 'manual') {
    audience.contact_ids = form.value.audience.contact_ids;
  } else if (form.value.audience_type === 'csv') {
    audience.rows = form.value.audience.rows;
  } else if (form.value.audience_type === 'segment') {
    audience.custom_filter_id = form.value.audience.custom_filter_id;
  }
  const steps_attributes = form.value.steps.map((s, i) => {
    const st = {
      position: i + 1,
      message_mode: 'text',
      content: s.content,
      delay_after_seconds: s.delay_after_seconds,
    };
    if (s.id) st.id = s.id;
    if (s.media.blob_id) st.attachment = s.media.blob_id;
    return st;
  });
  removedStepIds.value.forEach(id =>
    steps_attributes.push({ id, _destroy: true })
  );
  const isTemplate = form.value.message_mode === 'template';
  const payload = {
    name: form.value.name,
    inbox_id: form.value.inbox_id,
    message_mode: form.value.message_mode,
    // no modo template o content vira o corpo preenchido (aparece no chat)
    content:
      isTemplate && templateBody.value
        ? filledTemplateBody()
        : form.value.content,
    template_name: form.value.template_name,
    audience_type: form.value.audience_type,
    audience,
    min_interval_seconds: form.value.min_interval_seconds,
    max_interval_seconds: form.value.max_interval_seconds,
    scheduled_at: form.value.scheduled_at || null,
    follow_up_flow_id: form.value.follow_up_flow_id || null,
    steps_attributes,
  };
  // so envia attachment quando ha upload NOVO (mantem a midia atual na edicao)
  if (media.value.blob_id) payload.attachment = media.value.blob_id;
  if (isTemplate && selectedTemplate.value) {
    // botoes parametrizados vao com o indice REAL do template (a Meta rejeita
    // template com botao de URL dinamica sem o parametro correspondente)
    const buttons = templateButtonFields.value.map(b => ({
      type: b.type,
      index: b.index,
      parameter: (form.value.template_button_params[b.index] || '').trim(),
    }));
    payload.template_params = {
      name: selectedTemplate.value.name,
      category: selectedTemplate.value.category,
      language: selectedTemplate.value.language,
      namespace: selectedTemplate.value.namespace || '',
      processed_params: {
        body: { ...form.value.template_vars },
        ...(buttons.length ? { buttons } : {}),
      },
    };
  }
  return { broadcast: payload };
};

const saveAndDispatch = async dispatch => {
  // sem o parametro do botao a Meta rejeita 100% dos envios — barra antes de
  // disparar/agendar (salvar rascunho incompleto continua permitido)
  if ((dispatch || form.value.scheduled_at) && missingButtonParams()) {
    useAlert('Preencha o valor dos botões do template antes de disparar');
    return;
  }
  // Publico "Segmento": so com a previa valida (segmento escolhido e sem condicao invalida).
  if (form.value.audience_type === 'segment' && !segmentPreview.value.valid) {
    useAlert(translate('DISPARADOR.SEGMENT_AUDIENCE.INVALID'));
    return;
  }
  saving.value = true;
  try {
    const payload = buildPayload();
    const { data } = editingId.value
      ? await BroadcastsAPI.update(editingId.value, payload)
      : await BroadcastsAPI.create(payload);
    if (form.value.scheduled_at) {
      useAlert(
        `Disparo agendado para ${formatSchedule(form.value.scheduled_at)}`
      );
    } else if (dispatch) {
      await BroadcastsAPI.trigger(data.id);
      useAlert('Disparo iniciado!');
    } else {
      useAlert(
        editingId.value ? 'Disparo atualizado' : 'Disparo salvo como rascunho'
      );
    }
    showForm.value = false;
    await fetchBroadcasts();
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.message ||
        'Erro ao salvar disparo'
    );
  } finally {
    saving.value = false;
  }
};

const dispatchExisting = async b => {
  try {
    await BroadcastsAPI.trigger(b.id);
    useAlert('Disparo iniciado!');
    await fetchBroadcasts();
  } catch (e) {
    useAlert('Erro ao disparar');
  }
};

const pauseBroadcast = async b => {
  try {
    await BroadcastsAPI.pause(b.id);
    useAlert('Disparo pausado');
    await fetchBroadcasts();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao pausar');
  }
};

const resumeBroadcast = async b => {
  try {
    await BroadcastsAPI.resume(b.id);
    useAlert('Disparo retomado');
    await fetchBroadcasts();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao retomar');
  }
};

const cancelBroadcast = async b => {
  if (
    !window.confirm(
      `Cancelar o disparo "${b.name}"? Os pendentes não serão enviados.`
    )
  )
    return;
  try {
    await BroadcastsAPI.cancel(b.id);
    useAlert('Disparo cancelado');
    await fetchBroadcasts();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao cancelar');
  }
};

const openReport = async b => {
  report.value = {
    open: true,
    broadcast: b,
    recipients: [],
    loading: true,
    metrics: null,
    metricsLoading: true,
  };
  reportTab.value = 'resumo';
  // destinatarios (tabela) e metricas (resumo) carregam em paralelo; se as
  // metricas falharem, a tabela ainda aparece.
  BroadcastsAPI.recipients(b.id)
    .then(({ data }) => {
      report.value.recipients = data;
    })
    .catch(() => useAlert('Erro ao carregar os destinatários'))
    .finally(() => {
      report.value.loading = false;
    });
  BroadcastsAPI.report(b.id)
    .then(({ data }) => {
      report.value.metrics = data;
    })
    .catch(() => {})
    .finally(() => {
      report.value.metricsLoading = false;
    });
};

const closeReport = () => {
  report.value = {
    open: false,
    broadcast: null,
    recipients: [],
    loading: false,
    metrics: null,
    metricsLoading: false,
  };
};

// formatadores pt-BR do relatorio (null/ausente -> '—')
const fmtInt = n =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('pt-BR');
const fmtPct = r =>
  r === null || r === undefined ? '—' : `${Math.round(r * 100)}%`;
const fmtBRL = n =>
  n === null || n === undefined
    ? '—'
    : Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPerMsg = n =>
  n === null || n === undefined
    ? '—'
    : `R$ ${Number(n).toLocaleString('pt-BR', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })}`;
const formatDuration = s => {
  if (s === null || s === undefined) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
};
// valor/infotext do card "Tempo de envio"
const timingValue = computed(() => {
  const t = report.value.metrics?.timing;
  if (!t || !t.started_at) return '—';
  return formatDuration(t.duration_seconds);
});
const timingInfo = computed(() => {
  const t = report.value.metrics?.timing;
  if (!t || !t.started_at) return 'Disparo não iniciado';
  const start = formatSchedule(t.started_at);
  return t.finished_at
    ? `${start} → ${formatSchedule(t.finished_at)}`
    : `Iniciado ${start}`;
});

// contadores completos vem do backend (stats); a lista de linhas e limitada
// a 1000 pra nao travar o navegador em audiencias enormes.
const reportCounts = computed(() => {
  const s = report.value.broadcast?.stats || {};
  return {
    sent: s.sent || 0,
    failed: s.failed || 0,
    pending: s.pending || 0,
    skipped: s.skipped || 0,
  };
});

const removeBroadcast = async b => {
  if (!window.confirm(`Remover o disparo "${b.name}"?`)) return;
  try {
    await BroadcastsAPI.delete(b.id);
    await fetchBroadcasts();
  } catch (e) {
    useAlert('Erro ao remover');
  }
};

// A lista do Disparador (kanban_campaigns) abre o disparo anterior aqui com ?edit=<id> ou
// ?report=<id>; ao fechar o formulario ou o relatorio, volta para ela.
const backToDisparador = () =>
  router.push({
    name: 'kanban_campaigns',
    params: { accountId: route.params.accountId },
  });
const openFromDisparador = () => {
  const { edit, report: reportId } = route.query;
  const broadcast = broadcasts.value.find(
    b => String(b.id) === (edit || reportId)
  );
  if (!broadcast) return;
  if (edit) {
    if (EDITABLE_STATUSES.includes(broadcast.status)) openEdit(broadcast);
    else backToDisparador();
    return;
  }
  openReport(broadcast);
};
watch(showForm, open => {
  if (!open && route.query.edit) backToDisparador();
});
watch(
  () => report.value.open,
  open => {
    if (!open && route.query.report) backToDisparador();
  }
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  fetchBroadcasts().then(openFromDisparador);
  FlowsAPI.get()
    .then(({ data }) => {
      accountFlows.value = data;
    })
    .catch(() => {});
});

onUnmounted(() => {
  isMounted = false;
  clearTimeout(pollTimer);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <header class="sticky top-0 z-10 px-6 bg-n-background">
      <div
        class="flex items-center justify-between w-full h-20 gap-2 max-w-[1200px] mx-auto"
      >
        <div class="flex flex-col">
          <span
            class="text-[23px] font-semibold tracking-[-0.025em] text-n-slate-12"
            >{{
              translate('LEGACY_AUTOMATION.BROADCASTS.READ_ONLY_TITLE')
            }}</span
          >
          <span class="text-[13.5px] text-n-slate-10">
            {{ translate('LEGACY_AUTOMATION.BROADCASTS.READ_ONLY_SUBTITLE') }}
          </span>
        </div>
        <Button
          v-if="showForm"
          label="Voltar para a lista"
          icon="i-lucide-arrow-left"
          variant="faded"
          color="slate"
          size="sm"
          @click="showForm = false"
        />
      </div>
    </header>
    <div class="flex-1 px-6 pb-8">
      <!-- Lista (cards) -->
      <div
        v-if="!showForm"
        class="flex flex-col gap-4 pt-1 max-w-[1200px] mx-auto w-full"
      >
        <!-- KPIs -->
        <div
          v-if="broadcasts.length"
          class="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <div class="rounded-xl border border-n-container bg-n-solid-1 p-4">
            <div class="mb-2 text-[12.5px] font-medium text-n-slate-11">
              Disparos
            </div>
            <div
              class="text-2xl font-semibold tracking-[-0.02em] text-n-slate-12"
            >
              {{ broadcasts.length }}
            </div>
          </div>
          <div class="rounded-xl border border-n-container bg-n-solid-1 p-4">
            <div class="mb-2 text-[12.5px] font-medium text-n-slate-11">
              Enviando
            </div>
            <div
              class="text-2xl font-semibold tracking-[-0.02em] text-n-slate-12"
            >
              {{ broadcasts.filter(x => x.status === 'processing').length }}
            </div>
          </div>
          <div class="rounded-xl border border-n-container bg-n-solid-1 p-4">
            <div class="mb-2 text-[12.5px] font-medium text-n-slate-11">
              Agendados
            </div>
            <div
              class="text-2xl font-semibold tracking-[-0.02em] text-n-slate-12"
            >
              {{ broadcasts.filter(x => x.status === 'scheduled').length }}
            </div>
          </div>
          <div class="rounded-xl border border-n-container bg-n-solid-1 p-4">
            <div class="mb-2 text-[12.5px] font-medium text-n-slate-11">
              Concluídos
            </div>
            <div
              class="text-2xl font-semibold tracking-[-0.02em] text-n-slate-12"
            >
              {{ broadcasts.filter(x => x.status === 'completed').length }}
            </div>
          </div>
        </div>

        <div
          v-for="b in broadcasts"
          :key="b.id"
          class="flex flex-col gap-3 rounded-xl border border-n-container bg-n-solid-1 px-[18px] py-4"
        >
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-2.5 min-w-0">
              <span
                class="grid size-9 shrink-0 place-content-center rounded-[10px] bg-n-blue-3"
              >
                <span class="i-lucide-send size-[18px] text-n-blue-9" />
              </span>
              <div class="flex min-w-0 flex-col">
                <span class="text-sm font-semibold text-n-slate-12 truncate">{{
                  b.name
                }}</span>
                <span class="text-xs text-n-slate-10 truncate">
                  {{ b.inbox_name }} ·
                  {{ b.recipients_count }} destinatários<template
                    v-if="b.status === 'scheduled' && b.scheduled_at"
                  >
                    · {{ formatSchedule(b.scheduled_at) }}</template
                  >
                </span>
              </div>
            </div>
            <div class="flex items-center gap-1.5 shrink-0">
              <span
                class="mr-1 inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold"
                :class="statusClass(b.status)"
              >
                {{ statusLabel(b.status) }}
              </span>
              <Button
                v-if="b.status === 'processing'"
                label="Pausar"
                icon="i-lucide-pause"
                variant="faded"
                color="amber"
                size="sm"
                @click="pauseBroadcast(b)"
              />
              <Button
                v-else-if="b.status === 'paused'"
                label="Retomar"
                icon="i-lucide-play"
                variant="faded"
                color="blue"
                size="sm"
                @click="resumeBroadcast(b)"
              />
              <Button
                v-else
                :label="
                  ['completed', 'failed', 'cancelled'].includes(b.status)
                    ? 'Re-disparar'
                    : 'Disparar'
                "
                icon="i-lucide-send"
                variant="faded"
                color="blue"
                size="sm"
                @click="dispatchExisting(b)"
              />
              <Button
                v-if="['processing', 'paused'].includes(b.status)"
                icon="i-lucide-ban"
                variant="ghost"
                color="ruby"
                size="sm"
                title="Cancelar"
                @click="cancelBroadcast(b)"
              />
              <Button
                v-if="EDITABLE_STATUSES.includes(b.status)"
                icon="i-lucide-pencil"
                variant="ghost"
                color="slate"
                size="sm"
                title="Editar"
                @click="openEdit(b)"
              />
              <Button
                icon="i-lucide-chart-column"
                variant="ghost"
                color="slate"
                size="sm"
                title="Relatório"
                @click="openReport(b)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                color="ruby"
                size="sm"
                title="Remover"
                @click="removeBroadcast(b)"
              />
            </div>
          </div>

          <div class="flex items-center gap-2.5">
            <div
              class="h-[7px] flex-1 overflow-hidden rounded-full bg-n-slate-3"
            >
              <div
                class="h-full rounded-full"
                :class="
                  b.status === 'completed' ? 'bg-n-teal-9' : 'bg-n-blue-9'
                "
                :style="{
                  width: `${b.recipients_count ? Math.min(100, Math.round(((b.stats?.sent || 0) / b.recipients_count) * 100)) : 0}%`,
                }"
              />
            </div>
            <span class="shrink-0 text-xs font-semibold text-n-slate-11">
              {{
                b.recipients_count
                  ? Math.min(
                      100,
                      Math.round(
                        ((b.stats?.sent || 0) / b.recipients_count) * 100
                      )
                    )
                  : 0
              }}%
            </span>
          </div>

          <div class="flex flex-wrap gap-4">
            <span class="text-xs text-n-slate-10"
              >Enviados
              <b class="text-n-teal-11">{{ b.stats?.sent || 0 }}</b></span
            >
            <span class="text-xs text-n-slate-10"
              >Falhas
              <b
                :class="b.stats?.failed ? 'text-n-ruby-11' : 'text-n-slate-12'"
                >{{ b.stats?.failed || 0 }}</b
              ></span
            >
            <span class="text-xs text-n-slate-10"
              >Pendentes
              <b class="text-n-slate-12">{{ b.stats?.pending || 0 }}</b></span
            >
            <span class="text-xs text-n-slate-10"
              >Intervalo
              <b class="text-n-slate-12"
                >{{ b.min_interval_seconds }}–{{ b.max_interval_seconds }}s</b
              ></span
            >
          </div>
        </div>

        <div
          v-if="!broadcasts.length && !loading"
          class="flex flex-col items-center justify-center gap-3 rounded-2xl border border-n-container bg-n-solid-1 py-24 text-center"
        >
          <span class="i-lucide-send size-10 text-n-slate-9" />
          <p class="text-base font-semibold text-n-slate-12">
            Nenhum disparo anterior
          </p>
          <p class="text-sm text-n-slate-11">
            {{ translate('LEGACY_AUTOMATION.BROADCASTS.EMPTY_READ_ONLY') }}
          </p>
        </div>
      </div>

      <!-- Formulário + preview -->
      <div
        v-else
        class="flex gap-6 items-start w-full max-w-[1200px] mx-auto pt-1"
      >
        <div class="flex-1 min-w-0 flex flex-col gap-4">
          <!-- Dados básicos -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-send size-4 text-n-slate-11" />
              <h2 class="text-[15px] font-semibold text-n-slate-12">
                {{ editingId ? 'Editar disparo' : 'Novo disparo' }}
              </h2>
            </div>
            <div class="grid gap-4 lg:grid-cols-2">
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Nome do disparo</span
                >
                <input
                  v-model="form.name"
                  placeholder="ex: Promoção de julho"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Canal (caixa de entrada)</span
                >
                <select
                  v-model="form.inbox_id"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                >
                  <option value="">Selecione...</option>
                  <option v-for="i in inboxes" :key="i.id" :value="i.id">
                    {{ i.name }}
                  </option>
                </select>
              </label>
            </div>
            <div
              v-if="isOfficialWhatsapp"
              class="flex items-center gap-2 rounded-lg bg-n-blue-3 px-3 py-2.5 text-xs text-n-blue-9"
            >
              <span class="i-lucide-badge-check size-4 shrink-0" />
              Canal da API oficial: o envio em massa usa somente
              <b>templates aprovados</b> pela Meta.
            </div>
          </div>

          <!-- Mensagem -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-lucide-message-square-text size-4 text-n-slate-11"
              />
              <h3 class="text-[15px] font-semibold text-n-slate-12">
                Mensagem
              </h3>
            </div>

            <template v-if="form.message_mode === 'template'">
              <label
                v-if="approvedTemplates.length"
                class="flex flex-col gap-1.5"
              >
                <span class="text-xs font-semibold text-n-slate-11"
                  >Template aprovado</span
                >
                <select
                  v-model="selectedTemplateId"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                >
                  <option value="">Selecione...</option>
                  <option
                    v-for="t in approvedTemplates"
                    :key="templateId(t)"
                    :value="templateId(t)"
                  >
                    {{ t.name }} ({{ t.language }})
                  </option>
                </select>
                <span v-if="templatePriceBadge" class="text-xs text-n-slate-11">
                  {{ templatePriceBadge }} (estimativa Meta/Brasil)
                </span>
                <TemplateAlerts
                  v-if="selectedTemplate"
                  :template="selectedTemplate"
                />
              </label>
              <label v-else class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Nome do template aprovado</span
                >
                <input
                  v-model="form.template_name"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                />
                <span class="text-xs text-n-slate-10">
                  Nenhum template sincronizado neste canal ainda (a
                  sincronização com a Meta é automática).
                </span>
              </label>

              <div
                v-if="templateBody"
                class="rounded-xl rounded-tl-[4px] border border-n-teal-5 bg-n-teal-2 px-4 py-3 text-sm text-n-slate-12 whitespace-pre-wrap"
              >
                {{ templateBody }}
              </div>

              <div v-if="templateVarKeys.length" class="flex flex-col gap-2">
                <span class="text-sm text-n-slate-11">
                  Variáveis do template — texto fixo ou uma variável de contato
                  (clique para inserir):
                </span>
                <div class="flex flex-wrap items-center gap-1.5">
                  <button
                    v-for="v in CONTACT_VARS"
                    :key="v.hint"
                    type="button"
                    :title="v.desc"
                    class="h-6 px-1.5 rounded-md bg-n-slate-3 text-xs font-mono text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12 transition-all"
                    @click="insertContactVar(v.hint)"
                  >
                    {{ v.hint }}
                  </button>
                </div>
                <label
                  v-for="k in templateVarKeys"
                  :key="k"
                  class="flex items-center gap-3"
                >
                  <span
                    class="text-xs font-mono text-n-slate-11 w-16 shrink-0"
                    >{{ varHint(k) }}</span
                  >
                  <input
                    v-model="form.template_vars[k]"
                    class="h-9 flex-1 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                    @focus="activeVarKey = k"
                  />
                </label>
                <span class="text-xs text-n-slate-10">
                  {{ primeiroNomeHint }} = só o primeiro nome · {{ nomeHint }} =
                  nome completo. Atributos adicionais do contato (ex: dados de
                  import/CRM) também funcionam como variáveis.
                </span>
              </div>

              <div
                v-if="templateButtonFields.length"
                class="flex flex-col gap-2"
              >
                <span class="text-sm text-n-slate-11">
                  Botões do template — obrigatório preencher o valor de cada um
                </span>
                <label
                  v-for="b in templateButtonFields"
                  :key="b.index"
                  class="flex flex-col gap-1"
                >
                  <span class="text-xs text-n-slate-11">
                    Botão "{{ b.label }}"
                    <template v-if="b.url"> — {{ b.url }}</template>
                  </span>
                  <input
                    v-model="form.template_button_params[b.index]"
                    :placeholder="
                      b.type === 'copy_code'
                        ? 'Código a copiar'
                        : 'Valor que completa a URL do botão'
                    "
                    class="h-9 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                  />
                </label>
                <span class="text-xs text-n-slate-10">
                  Aceita variáveis ({{ primeiroNomeHint }}, {{ nomeHint }}...) e
                  colunas do CSV — o valor é resolvido por contato no envio.
                </span>
              </div>
            </template>

            <template v-else>
              <div class="flex flex-col gap-1.5">
                <span class="text-sm text-n-slate-11">
                  Texto (variações: {{ spinHint }})
                </span>
                <div class="flex flex-wrap items-center gap-1.5">
                  <span class="text-xs text-n-slate-10"
                    >Variáveis (clique para inserir):</span
                  >
                  <button
                    v-for="v in CONTACT_VARS"
                    :key="v.hint"
                    type="button"
                    :title="v.desc"
                    class="h-6 px-1.5 rounded-md bg-n-slate-3 text-xs font-mono text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12 transition-all"
                    @click="insertContactVar(v.hint)"
                  >
                    {{ v.hint }}
                  </button>
                </div>
                <textarea
                  v-model="form.content"
                  rows="4"
                  placeholder="Escreva a mensagem principal..."
                  class="p-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                />
              </div>

              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Mídia (opcional — imagem, áudio, vídeo ou documento)</span
                >
                <input
                  type="file"
                  accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
                  class="text-xs text-n-slate-11 file:mr-3 file:px-3 file:h-8 file:rounded-lg file:border-0 file:bg-n-slate-3 file:text-n-slate-12 file:text-xs file:font-medium file:cursor-pointer"
                  @change="handleMedia"
                />
                <span v-if="mediaUploading" class="text-xs text-n-slate-11">
                  Carregando...
                </span>
                <span v-else-if="media.filename" class="text-xs text-n-teal-11">
                  ✓ {{ media.filename }}
                </span>
              </label>

              <!-- Sequência de mensagens -->
              <div class="flex flex-col gap-3 rounded-xl bg-n-slate-2 p-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span
                      class="i-lucide-list-ordered size-4 text-n-slate-11"
                    />
                    <span class="text-sm font-medium text-n-slate-12"
                      >Sequência de mensagens</span
                    >
                  </div>
                  <Button
                    label="Adicionar mensagem"
                    icon="i-lucide-plus"
                    variant="link"
                    size="sm"
                    @click="addStep"
                  />
                </div>
                <p v-if="!form.steps.length" class="text-xs text-n-slate-11">
                  Opcional: envie mensagens de follow-up após a principal, com
                  atraso entre elas.
                </p>
                <div
                  v-for="(s, idx) in form.steps"
                  :key="idx"
                  class="flex flex-col gap-2 rounded-lg border border-n-weak p-3 bg-n-solid-1"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium text-n-slate-11"
                      >Mensagem {{ idx + 2 }}</span
                    >
                    <Button
                      icon="i-lucide-trash-2"
                      variant="ghost"
                      color="ruby"
                      size="xs"
                      @click="removeStep(idx)"
                    />
                  </div>
                  <textarea
                    v-model="s.content"
                    rows="2"
                    placeholder="Texto da mensagem"
                    class="p-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                  />
                  <div class="flex items-center gap-3 flex-wrap">
                    <label
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      Atraso antes (s)
                      <input
                        v-model.number="s.delay_after_seconds"
                        type="number"
                        min="0"
                        class="w-20 h-8 px-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 focus:outline-n-blue-9 transition-all"
                      />
                    </label>
                    <input
                      type="file"
                      accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
                      class="text-xs text-n-slate-11 file:mr-3 file:px-3 file:h-8 file:rounded-lg file:border-0 file:bg-n-slate-3 file:text-n-slate-12 file:text-xs file:font-medium file:cursor-pointer"
                      @change="e => handleStepMedia(e, idx)"
                    />
                    <span
                      v-if="s.media.filename"
                      class="text-xs text-n-teal-11 truncate"
                      >✓ {{ s.media.filename }}</span
                    >
                  </div>
                </div>
              </div>
            </template>

            <div class="border-t border-n-slate-3 pt-4 flex flex-col gap-1.5">
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Continuar no flow após a resposta (opcional)</span
                >
                <select
                  v-model="form.follow_up_flow_id"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all lg:max-w-sm"
                >
                  <option value="">Nenhum — só enviar o disparo</option>
                  <option
                    v-for="fl in accountFlows"
                    :key="fl.id"
                    :value="fl.id"
                  >
                    {{ fl.name }}
                  </option>
                </select>
              </label>
              <p class="text-xs text-n-slate-10">
                Quando o cliente <b>responder</b> ao disparo, a conversa
                continua neste flow (estilo typebot). Dica: comece o flow com um
                nó <b>Pergunta</b> — deixe o texto dela vazio para usar a
                resposta ao próprio disparo como ramificação.
              </p>
            </div>
          </div>

          <!-- Público-alvo -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-users size-4 text-n-slate-11" />
              <h3 class="text-[15px] font-semibold text-n-slate-12">
                Público-alvo
              </h3>
            </div>
            <select
              v-model="form.audience_type"
              class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all lg:max-w-sm"
            >
              <option value="all">Todos os contatos (com telefone)</option>
              <option value="labels">Por etiqueta</option>
              <option value="csv">Importar planilha (CSV)</option>
              <option value="segment">
                {{ $t('DISPARADOR.SEGMENT_AUDIENCE.OPTION') }}
              </option>
            </select>

            <SegmentAudiencePicker
              v-if="form.audience_type === 'segment'"
              v-model="form.audience.custom_filter_id"
              class="lg:max-w-xl"
              @result="segmentPreview = $event"
            />

            <div
              v-if="form.audience_type === 'labels'"
              class="flex flex-wrap gap-2"
            >
              <label
                v-for="l in labels"
                :key="l.id"
                class="inline-flex items-center gap-1.5 h-8 px-3 rounded-full outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-11 cursor-pointer has-[:checked]:outline-n-blue-9 has-[:checked]:text-n-slate-12"
              >
                <input
                  v-model="form.audience.label_ids"
                  type="checkbox"
                  :value="l.id"
                  class="accent-n-blue-9"
                />
                {{ l.title }}
              </label>
              <p v-if="!labels.length" class="text-xs text-n-slate-10">
                Nenhuma etiqueta cadastrada.
              </p>
            </div>

            <div
              v-else-if="form.audience_type === 'csv'"
              class="flex flex-col gap-2"
            >
              <div class="flex items-center gap-3 flex-wrap">
                <input
                  type="file"
                  accept=".csv"
                  class="text-xs text-n-slate-11 file:mr-3 file:px-3 file:h-8 file:rounded-lg file:border-0 file:bg-n-slate-3 file:text-n-slate-12 file:text-xs file:font-medium file:cursor-pointer"
                  @change="handleCsv"
                />
                <Button
                  label="Baixar modelo (CSV)"
                  icon="i-lucide-download"
                  variant="link"
                  size="sm"
                  @click="downloadCsvModel"
                />
              </div>
              <span
                v-if="form.audience.rows.length"
                class="text-xs text-n-teal-11"
              >
                ✓ {{ form.audience.rows.length }} contatos carregados
              </span>
              <p class="text-xs text-n-slate-10">
                Obrigatória a coluna <b>telefone</b> com DDI (ex:
                +5511999990001). Colunas extras viram variáveis na mensagem —
                ex: coluna "empresa" → {{ empresaHint }}.
              </p>
            </div>

            <p
              v-if="form.audience_type === 'all'"
              class="flex items-center gap-2 text-xs text-n-amber-11"
            >
              <span class="i-lucide-triangle-alert size-4 shrink-0" />
              Vai enviar para TODOS os contatos com telefone. Confira antes de
              disparar.
            </p>
          </div>

          <!-- Envio -->
          <div
            class="rounded-2xl border border-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-clock size-4 text-n-slate-11" />
              <h3 class="text-[15px] font-semibold text-n-slate-12">Envio</h3>
            </div>
            <div class="grid gap-4 lg:grid-cols-3">
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Intervalo mín (s)</span
                >
                <input
                  v-model.number="form.min_interval_seconds"
                  type="number"
                  min="0"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Intervalo máx (s)</span
                >
                <input
                  v-model.number="form.max_interval_seconds"
                  type="number"
                  min="1"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Agendar (opcional)</span
                >
                <input
                  v-model="form.scheduled_at"
                  type="datetime-local"
                  class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
                />
              </label>
            </div>
            <p class="text-xs text-n-slate-10">
              O intervalo aleatório entre mensagens protege seu número contra
              bloqueios (anti-ban).
            </p>
            <div class="flex gap-3">
              <Button
                :label="
                  form.scheduled_at ? 'Salvar e agendar' : 'Salvar e disparar'
                "
                :icon="
                  form.scheduled_at ? 'i-lucide-alarm-clock' : 'i-lucide-send'
                "
                :is-loading="saving"
                @click="saveAndDispatch(true)"
              />
              <Button
                :label="editingId ? 'Salvar alterações' : 'Salvar rascunho'"
                variant="faded"
                color="slate"
                :is-loading="saving"
                @click="saveAndDispatch(false)"
              />
              <Button
                label="Cancelar"
                variant="ghost"
                color="slate"
                @click="showForm = false"
              />
            </div>
          </div>
        </div>

        <!-- Preview estilo WhatsApp -->
        <div class="hidden lg:block w-96 2xl:w-[420px] shrink-0">
          <div class="sticky top-2 flex flex-col gap-2">
            <div
              class="rounded-2xl border border-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
            >
              <span class="text-xs font-semibold text-n-slate-11"
                >Pré-visualização</span
              >
              <div
                class="flex items-center gap-2 border-b border-n-slate-3 pb-3"
              >
                <span
                  class="size-8 rounded-full bg-n-teal-3 flex items-center justify-center text-n-teal-11 text-xs font-semibold"
                  >M</span
                >
                <div class="flex flex-col leading-tight">
                  <span class="text-xs font-semibold text-n-slate-12"
                    >Maria (exemplo)</span
                  >
                  <span class="text-[10px] text-n-slate-10">online</span>
                </div>
              </div>
              <div class="min-h-[320px] flex flex-col justify-end gap-2">
                <div
                  class="self-end max-w-[85%] rounded-xl rounded-tr-[4px] border border-n-teal-5 bg-n-teal-2 px-3 py-2"
                >
                  <img
                    v-if="mediaIsImage"
                    :src="media.file_url"
                    class="rounded-lg mb-2 max-h-40 w-full object-cover"
                  />
                  <div
                    v-else-if="media.filename"
                    class="flex items-center gap-2 mb-2 text-xs text-n-slate-11"
                  >
                    <span>📎</span
                    ><span class="truncate">{{ media.filename }}</span>
                  </div>
                  <p
                    class="text-sm text-n-slate-12 whitespace-pre-wrap break-words"
                  >
                    {{ previewText || 'Sua mensagem aparece aqui…' }}
                  </p>
                  <span
                    class="block text-right text-[10px] text-n-slate-10 mt-1"
                    >agora ✓✓</span
                  >
                  <div
                    v-if="previewButtons.length"
                    class="mt-2 -mx-3 -mb-2 border-t border-n-teal-5 flex flex-col divide-y divide-n-teal-5"
                  >
                    <span
                      v-for="(bt, i) in previewButtons"
                      :key="i"
                      class="py-2 text-center text-sm font-medium text-n-blue-9"
                    >
                      {{ bt }}
                    </span>
                  </div>
                </div>

                <template v-for="(st, i) in previewSteps" :key="i">
                  <span class="self-center text-[10px] text-n-slate-9"
                    >⏱ após {{ st.delay }}s</span
                  >
                  <div
                    class="self-end max-w-[85%] rounded-xl rounded-tr-[4px] border border-n-teal-5 bg-n-teal-2 px-3 py-2"
                  >
                    <img
                      v-if="st.isImage"
                      :src="st.media.file_url"
                      class="rounded-lg mb-2 max-h-40 w-full object-cover"
                    />
                    <div
                      v-else-if="st.media.filename"
                      class="flex items-center gap-2 mb-2 text-xs text-n-slate-11"
                    >
                      <span>📎</span
                      ><span class="truncate">{{ st.media.filename }}</span>
                    </div>
                    <p
                      class="text-sm text-n-slate-12 whitespace-pre-wrap break-words"
                    >
                      {{ st.text || '(sem texto)' }}
                    </p>
                    <span
                      class="block text-right text-[10px] text-n-slate-10 mt-1"
                      >agora ✓✓</span
                    >
                  </div>
                </template>
              </div>
            </div>
            <span class="text-[11px] text-n-slate-10">
              {{ primeiroNomeHint }} vira o primeiro nome, {{ nomeHint }} o nome
              completo · {{ spinHint }} sorteia uma variação no envio.
            </span>
          </div>
        </div>
      </div>

      <!-- Modal de relatório -->
      <div
        v-if="report.open"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        @click.self="closeReport"
      >
        <div
          class="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl bg-n-solid-1 shadow-xl border border-n-container overflow-hidden"
        >
          <div class="flex items-start justify-between gap-3 px-6 pt-5 pb-4">
            <div class="flex items-center gap-3 min-w-0">
              <span
                class="flex items-center justify-center size-10 rounded-[10px] bg-n-blue-3 shrink-0"
              >
                <span class="i-lucide-chart-column size-5 text-n-blue-9" />
              </span>
              <div class="flex flex-col min-w-0">
                <h2 class="text-[15px] font-semibold text-n-slate-12 truncate">
                  Relatório — {{ report.broadcast?.name }}
                </h2>
                <span
                  class="text-xs font-semibold inline-flex items-center w-fit h-6 px-2.5 mt-0.5 rounded-full"
                  :class="statusClass(report.broadcast?.status)"
                >
                  {{ statusLabel(report.broadcast?.status) }}
                </span>
              </div>
            </div>
            <Button
              icon="i-lucide-x"
              variant="ghost"
              color="slate"
              size="sm"
              @click="closeReport"
            />
          </div>

          <!-- Abas internas -->
          <div class="flex items-center gap-1 px-6 pb-3">
            <button
              type="button"
              class="px-3 h-8 rounded-full text-sm transition-colors"
              :class="
                reportTab === 'resumo'
                  ? 'bg-n-blue-3 text-n-blue-9 font-semibold'
                  : 'text-n-slate-11 hover:bg-n-slate-2 font-medium'
              "
              @click="reportTab = 'resumo'"
            >
              Resumo
            </button>
            <button
              type="button"
              class="px-3 h-8 rounded-full text-sm transition-colors"
              :class="
                reportTab === 'destinatarios'
                  ? 'bg-n-blue-3 text-n-blue-9 font-semibold'
                  : 'text-n-slate-11 hover:bg-n-slate-2 font-medium'
              "
              @click="reportTab = 'destinatarios'"
            >
              Destinatários
            </button>
          </div>

          <!-- Aba Resumo -->
          <div
            v-if="reportTab === 'resumo'"
            class="flex-1 overflow-auto border-t border-n-slate-3 px-6 py-5"
          >
            <div
              v-if="report.metricsLoading"
              class="flex items-center justify-center gap-2 p-10 text-sm text-n-slate-11"
            >
              <span class="i-lucide-loader-circle size-4 animate-spin" />
              Carregando métricas...
            </div>
            <div
              v-else-if="!report.metrics"
              class="p-10 text-center text-sm text-n-slate-11"
            >
              Não foi possível carregar as métricas deste disparo.
            </div>
            <template v-else>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-5">
                <ReportMetricCard
                  label="Disparados"
                  :value="fmtInt(report.metrics.volume?.dispatched)"
                  info-text="Total processado no disparo"
                />
                <ReportMetricCard
                  label="Entregues"
                  :value="fmtInt(report.metrics.delivery?.delivered)"
                  :info-text="`${fmtPct(report.metrics.delivery?.delivery_rate)} dos disparados`"
                />
                <ReportMetricCard
                  label="Lidas"
                  :value="fmtInt(report.metrics.delivery?.read)"
                  :info-text="`${fmtPct(report.metrics.delivery?.open_rate)} das entregues`"
                />
                <div>
                  <ReportMetricCard
                    label="Não entregues"
                    :value="fmtInt(report.metrics.delivery?.failed)"
                    info-text="Falhas confirmadas pela Meta"
                  />
                  <p class="mt-1 text-xs text-n-slate-10">
                    nº inválido:
                    {{ fmtInt(report.metrics.failures?.invalid) }} ·
                    recuperáveis:
                    {{ fmtInt(report.metrics.failures?.recoverable) }}
                  </p>
                </div>
                <ReportMetricCard
                  v-if="report.metrics.volume?.marketing_opt_out"
                  :label="
                    translate(
                      'WHATSAPP_TEMPLATES.MANAGER.ALERT.OPT_OUT_SKIPPED'
                    )
                  "
                  :value="fmtInt(report.metrics.volume.marketing_opt_out)"
                  :info-text="
                    translate(
                      'WHATSAPP_TEMPLATES.MANAGER.ALERT.OPT_OUT_SKIPPED_HINT'
                    )
                  "
                />
                <ReportMetricCard
                  v-if="report.metrics.delivery?.awaiting"
                  label="Aguardando confirmação"
                  :value="fmtInt(report.metrics.delivery?.awaiting)"
                  info-text="Sem confirmação de entrega ainda"
                />
                <ReportMetricCard
                  label="Respostas"
                  :value="fmtInt(report.metrics.engagement?.replies)"
                  info-text="Clientes que responderam ao disparo"
                />
                <ReportMetricCard
                  label="Opt-out"
                  :value="fmtInt(report.metrics.engagement?.opt_out)"
                  :info-text="`${fmtInt(report.metrics.engagement?.opt_out_contacts)} contatos`"
                />
                <ReportMetricCard
                  label="Tempo de envio"
                  :value="timingValue"
                  :info-text="timingInfo"
                />
                <ReportMetricCard
                  label="Custo estimado"
                  :value="fmtBRL(report.metrics.cost?.estimate_brl)"
                  :info-text="`estimativa · ${fmtPerMsg(report.metrics.cost?.per_message_brl)}/entregue`"
                />
              </div>

              <!-- Falhas por código -->
              <div v-if="report.metrics.failures?.by_code?.length" class="mt-6">
                <h3 class="text-sm font-medium text-n-slate-12 mb-2">
                  Falhas por código
                </h3>
                <table class="w-full text-sm">
                  <thead>
                    <tr
                      class="text-left text-[11px] uppercase tracking-[0.04em] font-semibold text-n-slate-9"
                    >
                      <th class="py-2 pr-3 font-semibold">Código</th>
                      <th class="py-2 pr-3 font-semibold">Motivo</th>
                      <th class="py-2 pr-3 font-semibold">Tipo</th>
                      <th class="py-2 font-semibold text-right">Qtd.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="f in report.metrics.failures.by_code"
                      :key="f.code"
                      class="border-t border-n-slate-3"
                    >
                      <td class="py-2 pr-3 font-mono text-xs text-n-slate-11">
                        {{ f.code }}
                      </td>
                      <td class="py-2 pr-3 text-n-slate-12">{{ f.label }}</td>
                      <td class="py-2 pr-3">
                        <span
                          class="text-xs font-semibold inline-flex items-center h-6 px-2.5 rounded-full"
                          :class="
                            f.recoverable
                              ? statusClass('completed')
                              : statusClass('failed')
                          "
                        >
                          {{ f.recoverable ? 'Recuperável' : 'Definitivo' }}
                        </span>
                      </td>
                      <td class="py-2 text-right font-medium text-n-slate-12">
                        {{ fmtInt(f.count) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>

          <!-- Aba Destinatários -->
          <div
            v-else
            class="flex-1 flex flex-col overflow-hidden border-t border-n-slate-3"
          >
            <div class="grid grid-cols-4 gap-3 px-6 py-4 shrink-0">
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl bg-n-teal-3 py-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-teal-11"
                  >{{ reportCounts.sent }}</span
                >
                <span class="text-xs text-n-slate-11">Enviados</span>
              </div>
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl bg-n-ruby-3 py-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-ruby-11"
                  >{{ reportCounts.failed }}</span
                >
                <span class="text-xs text-n-slate-11">Falhas</span>
              </div>
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl bg-n-amber-3 py-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-amber-11"
                  >{{ reportCounts.pending }}</span
                >
                <span class="text-xs text-n-slate-11">Pendentes</span>
              </div>
              <div
                class="flex flex-col items-center gap-0.5 rounded-xl bg-n-slate-3 py-3"
              >
                <span
                  class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
                  >{{ reportCounts.skipped }}</span
                >
                <span class="text-xs text-n-slate-11">Ignorados</span>
              </div>
            </div>

            <div class="flex-1 overflow-auto border-t border-n-slate-3">
              <div
                v-if="report.loading"
                class="flex items-center justify-center gap-2 p-10 text-sm text-n-slate-11"
              >
                <span class="i-lucide-loader-circle size-4 animate-spin" />
                Carregando...
              </div>
              <table v-else class="w-full text-sm">
                <thead class="sticky top-0 bg-n-slate-2 z-10">
                  <tr
                    class="text-left text-[11px] uppercase tracking-[0.04em] font-semibold text-n-slate-9"
                  >
                    <th class="px-6 py-3 font-semibold">Telefone</th>
                    <th class="px-3 py-3 font-semibold">Status</th>
                    <th class="px-3 py-3 font-semibold">Erro</th>
                    <th class="px-6 py-3 font-semibold text-right">
                      Enviado em
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="r in report.recipients"
                    :key="r.id"
                    class="border-t border-n-slate-3 hover:bg-n-slate-2 transition-colors"
                  >
                    <td class="px-6 py-3 font-medium text-n-slate-12">
                      {{ r.phone_number }}
                    </td>
                    <td class="px-3 py-3">
                      <span
                        class="text-xs font-semibold inline-flex items-center h-6 px-2.5 rounded-full"
                        :class="
                          statusClass(
                            r.status === 'sent'
                              ? 'completed'
                              : r.status === 'failed'
                                ? 'failed'
                                : r.status === 'pending'
                                  ? 'scheduled'
                                  : 'draft'
                          )
                        "
                      >
                        {{ statusLabel(r.status) }}
                      </span>
                    </td>
                    <td
                      class="px-3 py-3 text-xs text-n-ruby-11 max-w-[220px] truncate"
                      :title="r.error"
                    >
                      {{ r.error || '—' }}
                    </td>
                    <td
                      class="px-6 py-3 text-xs text-n-slate-11 text-right whitespace-nowrap"
                    >
                      {{ r.sent_at ? formatSchedule(r.sent_at) : '—' }}
                    </td>
                  </tr>
                  <tr v-if="!report.recipients.length">
                    <td
                      colspan="4"
                      class="px-6 py-12 text-center text-sm text-n-slate-11"
                    >
                      Nenhum destinatário resolvido.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
